import { createProfessionalExporter } from '../../utils/professionalExport';
import { formatCurrency } from './receptionistData';
import { getGymMemberId } from '../admin/members/memberData';
import {
  membershipStartDisplay,
  membershipExpiryDisplay,
  membershipDateDisplay,
  membershipPaymentNoteDisplay,
} from '../../utils/membershipDates';
import { PAYMENT_METHOD_OPTIONS, splitAmountForMethod } from '../../utils/splitPaymentUtils';

const methodHeaders = PAYMENT_METHOD_OPTIONS.map((m) => m.label);

const headers = [
  'Entry Date',
  'CR. No.',
  'Serial No',
  'Member',
  'CNIC',
  'Phone',
  'Gender',
  'DOB',
  'Trainer',
  'Package',
  'Duration',
  'Start Date',
  'Expiry',
  'Registration Fee',
  'Package Fee',
  'Amount Paid',
  ...methodHeaders,
  'Pending',
  'Payment Note',
  'Status',
];

function getMembershipStatus(membership) {
  if (membership.status === 'cancelled') return 'Cancelled';
  if (membership.frozen) return 'Frozen';
  if (membership.status === 'expired' || membership.status === 'due') {
    return membership.status === 'due' ? 'Due' : 'Expired';
  }
  return 'Active';
}

/** Same serial as membership table (ID-5493). */
function membershipSerialExport(m) {
  return getGymMemberId({
    serialNo: m?.serialNo ?? m?.serial_no,
    gym_member_id: m?.gym_member_id || m?.baseGymId,
    gymId: m?.gymId,
  }) || '—';
}

/** CR. No. = invoice_id on the payment. */
function membershipInvoiceId(m) {
  const invoice = String(m?.invoiceId || '').trim();
  if (invoice && invoice !== '—') return invoice;
  const paymentRef = String(m?.paymentId || m?.paymentRef || '').trim();
  if (paymentRef && paymentRef !== '—' && !/^[0-9a-f-]{36}$/i.test(paymentRef)) {
    return paymentRef;
  }
  // Fallback: use serial so Excel always has an ID column when invoice is blank
  const serial = membershipSerialExport(m);
  return serial !== '—' ? serial : '—';
}

const exporter = createProfessionalExporter({
  title: 'Membership List',
  headers,
  slug: 'memberships',
  getRows: (list) => list.map((m) => [
    membershipDateDisplay(m),
    membershipInvoiceId(m),
    membershipSerialExport(m),
    m.member || '—',
    m.cnic || '—',
    m.phone || '—',
    m.gender || '—',
    m.dob || '—',
    m.trainer || '—',
    m.package || '—',
    m.duration || '—',
    membershipStartDisplay(m),
    membershipExpiryDisplay(m),
    formatCurrency(m.registrationFee),
    formatCurrency(m.packageFee),
    formatCurrency(m.totalPaid),
    ...PAYMENT_METHOD_OPTIONS.map(({ key }) => {
      const amt = splitAmountForMethod(m, key);
      return amt > 0 ? formatCurrency(amt) : '—';
    }),
    formatCurrency(m.pendingAmount),
    membershipPaymentNoteDisplay(m),
    getMembershipStatus(m),
  ]),
  getSummary: (list) => [
    `Total memberships: ${list.length}`,
    `Active: ${list.filter((m) => getMembershipStatus(m) === 'Active').length}`,
  ],
});

export const printMembershipList = (data) => exporter.print(data);
export const exportMembershipCsv = (data) => exporter.csv(data);
export const exportMembershipExcel = (data) => exporter.excel(data);
export const exportMembershipPdf = (data) => exporter.pdf(data);
export const exportMemberships = (data) => exporter.export(data);
